import React, { useCallback, useRef, useState } from 'react';

import {
	Autocomplete,
	AutocompleteProps,
	MenuItem,
	styled,
	TextField,
	useTheme,
} from '@mui/material';
import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Box } from '@mui/system';
import { JSX } from 'react/jsx-runtime';

import VectorImage from '~components/media/vectorImage';
import { useDebounceCallback } from '~hooks';
import { BaseReference } from '~interfaces';
import { Dimens } from '~themes';

interface AsyncAutocompleteProps<T extends BaseReference> extends Omit<
	AutocompleteProps<T, false, boolean | undefined, undefined>,
	'renderInput'
> {
	/**
	 * Label that animated to the top when gaining focus.
	 */
	label?: string;
	/**
	 * Placeholder text that is visible in the textfield until there is a non empty value.
	 */
	placeholder?: string;
	/**
	 * Enable to trigger and onInputChange on a manual search
	 */
	enableManualSearch?: boolean;
	/**
	 * Whether or not to debounce the input. True by default, but it recommended to
	 * disable when there is no network request tied to the search.
	 */
	debounce?: boolean;
	/**
	 * The delay in milliseconds to use when not performing manual searches
	 */
	delay?: number;
	/**
	 * Enable infinite scroll functionality. Use this with the
	 * onOverflow handler
	 */
	enableInfiniteScroll?: boolean;
	/**
	 * An event raised when the list overflows. E.g. on a scroll
	 */
	onOverflow?: (e: React.WheelEvent<HTMLUListElement>) => void;
	/**
	 * An event where the user selected a value from the list
	 */
	onOptionSelected: (value: T | undefined) => void;
	/**
	 * An event where the user cleared the input
	 */
	required?: boolean;
}

const AsyncAutocomplete = <T extends BaseReference>({
	label,
	placeholder,
	enableManualSearch = false,
	enableInfiniteScroll = true,
	debounce = true,
	onOverflow,
	onInputChange,
	onOptionSelected,
	required = false,
	delay,
	...autocompleteProps
}: AsyncAutocompleteProps<T>) => {
	const manualSearchValueRef = useRef<string>('');
	const debounceCallback = useDebounceCallback();
	const { palette } = useTheme();

	const [internalValue, setInternalValue] = useState<T | null>(null);

	const isControlled = Object.prototype.hasOwnProperty.call(autocompleteProps, 'value');

	const externalValue =
		isControlled ? ((autocompleteProps as { value?: T | null }).value ?? null) : null;
	const effectiveValue = (isControlled ? externalValue : internalValue) ?? null;
	const { value: _ignoredValueProp, ...forwardProps } = autocompleteProps;

	/**
	 * Handle scrolling of the wheel
	 * @returns
	 */
	const handleWheel = (event: React.WheelEvent<HTMLUListElement>) => {
		if (!enableInfiniteScroll) {
			return;
		}

		const ulElement = (event.target as HTMLLIElement).parentElement as HTMLUListElement;
		const isBottomList = ulElement.scrollHeight - ulElement.scrollTop - 1 <= ulElement.clientHeight;
		if (isBottomList && !autocompleteProps.loading && onOverflow) {
			onOverflow(event);
		}
	};

	const handleInputChange: AutocompleteProps<
		T,
		false,
		boolean | undefined,
		undefined
	>['onInputChange'] = (event, value, reason) => {
		if (reason === 'blur' || reason === 'reset') {
			if (reason === 'reset') {
				onInputChange?.(event, '', reason);
			}
			return; // ignore to leave input
		}

		manualSearchValueRef.current = value;
		if (reason === 'clear') {
			if (!isControlled) {
				setInternalValue(null);
			}
			onOptionSelected(undefined);
		}

		if (!enableManualSearch && onInputChange != null) {
			debounceCallback(() => onInputChange(event, value, reason), debounce ? delay : 0);
		} else {
			manualSearchValueRef.current = value;
		}
	};

	const handleManualSearch = () => {
		const searchValue = manualSearchValueRef.current ?? '';
		if (searchValue.trim().length === 0) {
			onOptionSelected(undefined);
		}
		onInputChange?.({} as React.SyntheticEvent, searchValue, 'input');
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter') {
			return;
		}
		if (!enableManualSearch) {
			return;
		}
		event.preventDefault();
		handleManualSearch();
	};

	const handleChange: AutocompleteProps<T, false, boolean | undefined, undefined>['onChange'] = (
		event,
		newValue,
		reason,
		details
	) => {
		if (!isControlled) {
			setInternalValue(newValue);
		}
		onOptionSelected(newValue ?? undefined);
		autocompleteProps.onChange?.(event, newValue, reason, details);
	};

	const getOptionLabel: AutocompleteProps<
		T,
		false,
		boolean | undefined,
		undefined
	>['getOptionLabel'] = (option) => option.name;

	const passthroughProps = forwardProps as Omit<
		AutocompleteProps<BaseReference, false, boolean | undefined, undefined>,
		'renderInput'
	>;

	// We always return all options, because we're always using controlled asyncAutocompletes.
	// Meaning all lists are already filtered.
	const filterOptions = (options: BaseReference[]) => {
		return options;
	};

	return (
		<StyledAutocomplete
			{...passthroughProps}
			value={effectiveValue as BaseReference | null}
			onInputChange={
				handleInputChange as AutocompleteProps<
					BaseReference,
					false,
					boolean | undefined,
					undefined
				>['onInputChange']
			}
			onChange={
				handleChange as AutocompleteProps<
					BaseReference,
					false,
					boolean | undefined,
					undefined
				>['onChange']
			}
			getOptionLabel={
				getOptionLabel as AutocompleteProps<
					BaseReference,
					false,
					boolean | undefined,
					undefined
				>['getOptionLabel']
			}
			filterOptions={filterOptions}
			onKeyDown={handleKeyDown}
			renderOption={(props, option: BaseReference) => {
				const typedOption = option as T;
				const { key, ...rest } = props;
				return (
					<StyledMenuItem
						key={option.id}
						{...rest}
						value={typedOption.id}
						sx={{
							// doesn't work in the styled component, no clue why. Took 2 hours to find this selector
							// I'm sticking with this
							'&.MuiAutocomplete-option[aria-selected="true"]': {
								backgroundColor: palette.bgTertiary.main,
								opacity: 1,
								mx: Dimens.spacingXs,
								borderRadius: Dimens.radiusSm,
								'&:hover': {
									backgroundColor: palette.bgTertiary.main,
									opacity: 1,
									borderRadius: Dimens.radiusSm,
								},
								'&.Mui-focused': {
									backgroundColor: palette.bgTertiary.main,
									opacity: 1,
									borderRadius: Dimens.radiusSm,
								},
							},
							'& .MuiAutocomplete-option[aria-selected="true"]': {
								backgroundColor: palette.bgTertiary.main,
								opacity: 1,
								mx: Dimens.spacingXs,
								borderRadius: Dimens.radiusSm,
								'&:hover': {
									backgroundColor: palette.bgTertiary.main,
									opacity: 1,
									borderRadius: Dimens.radiusSm,
								},
								'&.Mui-focused': {
									backgroundColor: palette.bgTertiary.main,
									opacity: 1,
									borderRadius: Dimens.radiusSm,
								},
							},
							'&.MuiAutocomplete-option': {
								mx: Dimens.spacingXs,
								'&.Mui-focused': {
									backgroundColor: palette.bgTertiary.main,
									opacity: 1,
									borderRadius: Dimens.radiusSm,
								},
							},
							'& .MuiAutocomplete-option': {
								mx: Dimens.spacingXs,
								'&.Mui-focused': {
									backgroundColor: palette.bgTertiary.main,
									opacity: 1,
									borderRadius: Dimens.radiusSm,
								},
							},
						}}
					>
						{typedOption.name}
					</StyledMenuItem>
				);
			}}
			clearIcon={<VectorImage name='icon/cross' size={Dimens.spacingXl} display='block' />}
			popupIcon={<VectorImage name='chevron/down' size={Dimens.spacingXl} display='block' />}
			renderInput={(params: AutocompleteRenderInputParams) => (
				<StyledTextField
					{...params}
					variant='outlined'
					label={label}
					placeholder={placeholder}
					required={required}
					sx={{
						// Prevents Mui's default blue focus outline
						'& .Mui-focused .MuiOutlinedInput-notchedOutline': {
							border: `2px solid ${palette.borderPrimarySolid.main}`,
							borderColor: palette.borderPrimarySolid.main + '!important',
							outline: 'none',
						},
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							border: `2px solid ${palette.borderPrimarySolid.main}`,
							borderColor: palette.borderPrimarySolid.main + '!important',
							outline: 'none',
						},
					}}
				/>
			)}
			slots={{
				// Wrapping it in a useCallback prevents weird scrolling behavior
				listbox: useCallback(
					(
						listBoxProps: JSX.IntrinsicAttributes &
							React.ClassAttributes<HTMLUListElement> &
							React.HTMLAttributes<HTMLUListElement>
					) => {
						return (
							<Box role='listbox' sx={{ maxHeight: '300px', padding: 0, margin: 0 }}>
								<ul {...listBoxProps} style={{ padding: 0, margin: 0 }}></ul>
							</Box>
						);
					},
					[]
				),
			}}
			slotProps={{
				listbox: {
					role: 'listbox',
					onWheelCapture: handleWheel,
				},
				popupIndicator: {
					style: {
						padding: 0,
						margin: 0,
					},
				},
			}}
		/>
	);
};

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
	...theme.typography.paragraph,
	color: theme.palette.textTertiary.main,
}));

const StyledAutocomplete = styled(
	Autocomplete<BaseReference, false, boolean | undefined, undefined>
)<AutocompleteProps<BaseReference, false, boolean | undefined, undefined>>(({ theme }) => ({
	...theme.typography.paragraph,
	color: theme.palette.textTertiary.main,
	'&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': {
		paddingRight: `${Dimens.spacingLg}`,
	},
	'&.MuiAutocomplete-root': {
		padding: 0,
		'& .MuiOutlinedInput-root': {
			padding: `${Dimens.spacingMd} ${Dimens.spacingLg} ${Dimens.spacingMd} ${Dimens.spacingLg}`,
			'& .MuiAutocomplete-input': {
				minHeight: '24px',
				padding: 0,
			},
			'& .MuiAutocomplete-endAdornment': {
				top: 0,
				bottom: 0,
				right: '12px',
				display: 'flex',
				alignItems: 'center',
				lineHeight: 0,
				transform: 'none',
			},
		},
		'& .MuiAutocomplete-clearIndicator': {
			visibility: 'visible',
			padding: '8px',
			marginRight: 0,
		},
	},
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
	'& .MuiInputBase': {
		'&-root': {
			padding: 0,
			outline: 'none',
			'&:hover .MuiOutlinedInput-notchedOutline': {
				border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
			},
			'&:focus .MuiOutlinedInput-notchedOutline': {
				border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
			},
			'&:active .MuiOutlinedInput-notchedOutline': {
				border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
			},
			'&:focus-visible .MuiOutlinedInput-notchedOutline': {
				border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
			},
		},
		'&-input': {
			...theme.typography.paragraph,
			color: theme.palette.textTertiary.main,
			padding: 0,
			'&::placeholder': {
				color: theme.palette.textPlaceholder.main,
				opacity: 1,
			},
		},
	},
	'& .MuiOutlinedInput': {
		'&-notchedOutline': {
			top: 0,
			border: `1px solid ${theme.palette.borderPrimary.main}`,
			'& legend': {
				// This is the mui label that places itself in the border.
				// But it leaves a gap in the border, so we hide it.
				display: 'none',
			},
		},
	},
	'&.MuiOutlinedInput': {
		'&-notchedOutline': {
			top: 0,
			border: `1px solid ${theme.palette.borderPrimary.main}`,
			'& legend': {
				// This is the mui label that places itself in the border.
				// But it leaves a gap in the border, so we hide it.
				display: 'none',
			},
		},
	},
	'& .MuiInputAdornment-root': {
		marginRight: Dimens.spacingMd,
	},
}));

export type { AsyncAutocompleteProps };

export default AsyncAutocomplete;
